/**
 * 가구 라이브러리 진입점 — 정의(pieces) + 재질(slots) + 커널(kernel)을 조립한다.
 */

import * as THREE from 'three';
import { MeshWriter, analyze, type ModelStats, type Slot } from './kernel';
import { attachGlb } from './glb';
import { slotMaterials } from './slots';
import { MANOR_PIECES, type Piece } from './pieces';
import { BANDAJI_PIECES } from './bandaji';
import { HOUSEHOLD_PIECES } from './household';
import { SAHOL_PIECES } from './sahol';
import { ARCH_PIECES } from './arch';
import { VARIANT_PIECES } from './variants';
import { SHAPE2_PIECES } from './shapes2';
import { FOOD_PIECES } from './food';
import { SAHOL_STORY_PIECES } from './saholStory';
import { SAHOL_VILLAGE_PIECES } from './saholVillage';
import { SAHOL_EVIDENCE_PIECES } from './saholEvidence';
import { SAHOL_PAPER_PIECES } from './saholPapers';
import { SAHOL_DEVICE_PIECES } from './saholDevices';
import { SAHOL_PERSONAL_PIECES } from './saholPersonal';
import { SAHOL_FIXTURE_PIECES } from './saholFixture';
import { SAHOL_SHEET_PIECES } from './saholSheets';
import { KEEPSAKE_PIECES } from './keepsakeBox';
import { FRAME_PIECES } from './photoFrame';
import { SHOERACK_PIECES } from './shoeRack';

/** 모든 컬렉션의 가구 */
export const PIECES: Piece[] = [
  ...MANOR_PIECES,
  ...BANDAJI_PIECES,
  ...HOUSEHOLD_PIECES,
  ...SAHOL_PIECES,
  ...SAHOL_STORY_PIECES,
  ...SAHOL_VILLAGE_PIECES,
  ...SAHOL_EVIDENCE_PIECES,
  ...SAHOL_PAPER_PIECES,
  ...SAHOL_DEVICE_PIECES,
  ...SAHOL_PERSONAL_PIECES,
  ...SAHOL_FIXTURE_PIECES,
  ...SAHOL_SHEET_PIECES,
  ...ARCH_PIECES,
  ...KEEPSAKE_PIECES,
  ...FRAME_PIECES,
  ...SHOERACK_PIECES,
  ...VARIANT_PIECES,
  ...SHAPE2_PIECES,
  ...FOOD_PIECES,
];
export const PIECE_BY_ID = Object.fromEntries(PIECES.map((p) => [p.id, p]));

/**
 * **치수에 맞춘 여닫이 규격** (2026-09-16). `Piece.openable` 의 pivot·inside 는 기본 치수(`dims`)에서 잰 수라,
 * 변형(좁은 옷장 w 0.8, 넓은 책상 w 1.8, 높은 협탁 h 0.72)이나 배치의 dims 덮어쓰기로 몸집이 달라지면
 * 경첩이 문짝에서 떨어져 허공에서 돈다. 원본 조각의 기본 치수 대비 비율(w→x, h→y, d→z)로 pivot 과
 * 담는 곳(at·w·h·d)을 늘이거나 줄인다 — 문짝은 몸통 가장자리에, 서랍은 칸 높이에 비례하므로 이 근사가 맞는다.
 * 치수를 안 바꾼 조각은 비율이 1 이라 그대로다.
 */
export function openSpecFor(pieceId: string, dims: Record<string, number> | undefined, part: string): NonNullable<Piece['openable']>[number] | undefined {
  const p = PIECE_BY_ID[pieceId];
  const spec = p?.openable?.find((o) => o.part === part);
  if (!p || !spec) return undefined;
  const base = PIECE_BY_ID[p.variantOf ?? p.id] ?? p;
  const eff: Record<string, number> = { ...p.dims, ...(dims ?? {}) };
  const ratio = (k: string) => (base.dims[k] && eff[k] ? eff[k] / base.dims[k] : 1);
  let sx = ratio('w'), sy = ratio('h'), sz = ratio('d');
  /* 상자·단처럼 `s` 하나로 균등 스케일하는 조각은 w/h/d 키가 없어 비율이 1 로 나온다 —
   * s 비율을 비어 있는 축의 폴백으로 쓴다 (2026-09-16) */
  const sR = ratio('s');
  if (sR !== 1) {
    if (!base.dims.w) sx = sR;
    if (!base.dims.h) sy = sR;
    if (!base.dims.d) sz = sR;
  }
  if (Math.abs(sx - 1) < 1e-6 && Math.abs(sy - 1) < 1e-6 && Math.abs(sz - 1) < 1e-6) return spec;
  const sc = (v: [number, number, number]): [number, number, number] => [v[0] * sx, v[1] * sy, v[2] * sz];
  return {
    ...spec,
    ...(spec.pivot ? { pivot: sc(spec.pivot) } : {}),
    ...(spec.inside ? { inside: { w: spec.inside.w * sx, h: spec.inside.h * sy, d: spec.inside.d * sz, at: sc(spec.inside.at) } } : {}),
  };
}
export {
  MANOR_PIECES,
  BANDAJI_PIECES,
  HOUSEHOLD_PIECES,
  SAHOL_PIECES,
  SAHOL_STORY_PIECES,
  SAHOL_VILLAGE_PIECES,
  SAHOL_EVIDENCE_PIECES,
  SAHOL_PAPER_PIECES,
  SAHOL_DEVICE_PIECES,
  SAHOL_PERSONAL_PIECES,
  SAHOL_FIXTURE_PIECES,
  SAHOL_SHEET_PIECES,
  ARCH_PIECES,
  KEEPSAKE_PIECES,
  FRAME_PIECES,
  SHOERACK_PIECES,
};
export type { Piece, ModelStats, Slot };

export interface BuiltPiece {
  group: THREE.Group;
  stats: ModelStats;
  /**
   * 이 조각이 차지하는 부피 (조각 로컬 좌표).
   *
   * **GLB 조각은 group 이 한동안 비어 있다** — 모델이 네트워크로 오는 중이라
   * `Box3().setFromObject(group)` 가 빈 상자를 준다. 충돌체를 그걸로 만들면
   * 물건을 통과해 걸어 다니게 된다(실제로 그랬다). 절차본으로 잰 값을 여기
   * 실어 보내서 부르는 쪽이 그걸 쓰게 한다.
   */
  fit: THREE.Box3;
}

/**
 * 가구 한 점을 만든다.
 * 슬롯당 메시 하나로 합쳐지므로 가구 1점의 draw call 은 보통 3~5개다.
 */
/**
 * **낡음을 정점색에 굽는다.** 재질 파이프라인은 「새 것」만 만든다 — 같은 상자 열일곱 개가
 * 같아 보이는 이유의 절반이 여기 있다. 세 가지만, 전부 곱셈이라 드로우콜·재질은 그대로다:
 *  1. 색조 — 조각마다 밝기 ±5% · 붉기/푸르기 ±2% (햇빛에 바랜 정도가 다르다)
 *  2. 접지 그림자 — 밑면 16cm 안이 어둡다(바닥에 놓인 것만. 매달린 것·벽걸이는 뺀다)
 *  3. 모서리 닳음 — 베벨 면(법선이 축과 안 맞는 면)이 살짝 밝다
 * 유리·화면·인쇄면은 건드리지 않는다 — 인쇄면은 색을 곱하면 글자가 탁해진다.
 */
const NO_WEATHER = new Set(['glass', 'screen', 'painted']);
function weather(geo: THREE.BufferGeometry, slot: string, seed: number, piece: Piece) {
  if (NO_WEATHER.has(slot)) return;
  const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const nor = geo.getAttribute('normal') as THREE.BufferAttribute | undefined;
  if (!col || !pos) return;
  const rnd = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); };
  const m = 1 + (rnd(1) - 0.5) * 0.10;
  const tr = m * (1 + (rnd(3) - 0.5) * 0.04), tg = m, tb = m * (1 + (rnd(7) - 0.5) * 0.04);
  const grounded = !piece.hangs && !piece.mount;
  for (let i = 0; i < col.count; i++) {
    let k = 1;
    if (grounded) { const y = pos.getY(i); k *= 0.78 + 0.22 * Math.min(1, Math.max(0, y / 0.16)); }
    if (nor) { const ax = Math.abs(nor.getX(i)), ay = Math.abs(nor.getY(i)), az = Math.abs(nor.getZ(i)); if (ax < 0.92 && ay < 0.92 && az < 0.92) k *= 1.08; }
    col.setXYZ(i, Math.min(1, col.getX(i) * tr * k), Math.min(1, col.getY(i) * tg * k), Math.min(1, col.getZ(i) * tb * k));
  }
  col.needsUpdate = true;
}

/* **조각 전용 재질은 조각 종류마다 한 번만 만든다** (2026-09-25). `materials()` 는 캔버스에 그림을 그려
 * 텍스처를 만든다(인쇄물·재·부적). 배치된 인스턴스마다 불렀더니 같은 그림을 수십 번 그리고 GPU 에도 수십 장
 * 올렸다 — 연탄재 하나만 2.4초(측정). 입력이 renderer 뿐이라 결과가 같다. 공용 슬롯 재질(slotMaterials)과 같은 방식 */
const pieceMatCache = new WeakMap<Piece, Partial<Record<Slot, THREE.Material>>>();
function pieceMaterials(piece: Piece, renderer: THREE.WebGLRenderer): Partial<Record<Slot, THREE.Material>> {
  let m = pieceMatCache.get(piece);
  if (!m) { m = piece.materials?.(renderer) ?? {}; pieceMatCache.set(piece, m); }
  return m;
}

/**
 * **자리에서 뽑는 seed** — 게임(world.ts)이 `dims.seed` 가 없는 조각에 쓰는 것. 감사 도구도 이걸 써야 게임과 같은 모양을 본다.
 * 솔버가 푼 조각은 `dims.seed` 가 생성 파일에 적혀 있어 이 식을 안 쓴다(자리를 옮겨도 모양이 안 바뀐다).
 */
export const placeSeed = (pos: readonly number[]) => Math.abs(Math.round(pos[0] * 73.1 + pos[2] * 19.7 + (pos[1] ?? 0) * 3.3)) % 9973;

export function buildPiece(
  piece: Piece,
  renderer: THREE.WebGLRenderer,
  overrides?: Record<string, number>,
  /** 자리가 주는 문자열 상태 (사흘 v8 — 분전반 `tripped` 회로 라벨 등). dims 는 숫자만이라 못 싣는다 */
  state?: Record<string, string>,
): BuiltPiece {
  // 가구 전용 재질(무신도 그림 등)은 공용 슬롯 재질을 덮어쓴다
  const mats = new Map(slotMaterials(renderer));
  for (const [slot, mat] of Object.entries(pieceMaterials(piece, renderer))) {
    if (mat) mats.set(slot as Slot, mat);
  }
  const writer = new MeshWriter();
  const dims0 = { ...piece.dims, ...overrides };
  /* **seed 로 조각마다 달라진다** (2026-09-14, Chris: 「다 너무 동일한 파일들, 동일한 나무 상자」).
   * 배치가 `dims.seed` 를 주면(world.ts 가 자리에서 뽑는다) 조각의 `vary` 가 치수를 흔들고,
   * 아래 `weather` 가 색조·접지 그림자·모서리 닳음을 정점색에 굽는다. 같은 조각 열일곱 개가
   * 같은 모양은 없어야 창고다. */
  const seed = dims0.seed ?? 0;
  const dims = piece.vary && dims0.seed !== undefined ? piece.vary(seed, dims0) : dims0;
  piece.build(writer, dims, state);

  const slots = writer.build();
  const movables = writer.buildMovables();
  if (dims0.seed !== undefined && !piece.noWeather) {
    for (const [slot, geo] of slots) weather(geo, slot, seed, piece);
    for (const [, mv] of movables) for (const [slot, geo] of mv) weather(geo, slot, seed, piece);
  }
  const group = new THREE.Group();
  group.name = piece.id;

  /* **움직이는 부품은 제 그룹으로 남는다** (2026-09-11) — `w.movable('플랩', …)` 로 감싼 것.
   * `Piece.openable` 이 이 그룹 이름으로 「어떻게 여는지」를 말하고, props.ts 가 그걸 돌린다.
   * 그룹 이름은 `open:<부품이름>` 이라 씬에서 찾기 쉽다. */
  for (const [name, mv] of movables) {
    const g = new THREE.Group();
    g.name = `open:${name}`;
    for (const [slot, geo] of mv) {
      const m = new THREE.Mesh(geo, mats.get(slot));
      m.name = slot; m.castShadow = true; m.receiveShadow = true;
      g.add(m);
    }
    /* **glb 조각의 절차 보조 부품** (2026-09-16): `w.movable` 은 glb 에서도 그린다.
     * Tripo GLB 는 노드가 하나라 부품별 여닫이가 불가능하다 — 모델에 없는 부품(북채·향·짚단
     * 같은 곁가지)을 절차로 얹어 그것을 여닫이로 쓴다. 본체 절차 메시는 여전히 안 붙는다. */
    group.add(g);
  }

  const drawn: THREE.Mesh[] = [];
  for (const [slot, geo] of slots) {
    const mesh = new THREE.Mesh(geo, mats.get(slot));
    mesh.name = slot;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    drawn.push(mesh);
    // **GLB 로 그릴 조각은 절차 메시를 씬에 넣지 않는다.** 아래 참고
    if (!piece.glb) group.add(mesh);
  }
  /**
   * 외부 모델이 있으면 **절차 메시를 아예 안 붙인다.**
   *
   * 처음에는 절차본을 그대로 두고 GLB 가 도착하면 `visible = false` 로 감췄다.
   * 그런데 **`shrineSet.ts` 의 `bakeFloor()` 가 `visible` 을 안 본다** — 층을
   * 통째로 순회해 지오메트리를 재질별로 합쳐 버린다. 순서가 이렇게 됐다:
   *
   *   1. 절차 메시 생성          2. 배칭에 흡수(원본은 removeFromParent)
   *   3. GLB 도착 → visible=false  ← **이미 녹아든 덩어리는 안 사라진다**
   *
   * 그래서 항아리도 신단도 작두도 전부 두 벌씩 겹쳐 서 있었다. 감추는 것으로는
   * 못 막는다 — 애초에 만들지 않아야 한다.
   *
   * 크기는 여전히 절차본이 정한다. 씬에 넣지 않아도 지오메트리의 bbox 는 잴 수
   * 있어서, `dims` 계약과 배치 좌표는 그대로 산다.
   *
   * 로드가 실패하면 그때 `drawn` 을 붙인다. 배칭은 이미 끝났으니 개별 draw call 로
   * 그려지지만, 맵에 구멍이 나는 것보다 낫다.
   */
  const fit = new THREE.Box3();
  if (piece.glb) {
    for (const m of drawn) {
      m.geometry.computeBoundingBox();
      if (m.geometry.boundingBox) fit.union(m.geometry.boundingBox);
    }
    // 조각 전체가 여닫이(액자처럼 `w.movable` 로만 이뤄진 것)면 `drawn` 이 비어 자리를 못 잰다 —
    // 움직이는 부품의 부피도 합친다 (2026-09-16, 액자가 안 보이던 원인)
    for (const [, mv] of movables) for (const [, geo] of mv) { geo.computeBoundingBox(); if (geo.boundingBox) fit.union(geo.boundingBox); }
    if (!fit.isEmpty()) {
      attachGlb(group, piece.glb, { min: fit.min, max: fit.max }, drawn);
    } else {
      // 잴 것이 없으면 GLB 를 앉힐 자리도 못 정한다. 절차본을 그대로 쓴다
      for (const m of drawn) group.add(m);
    }
  }

  /* **진단(stats)은 필요할 때만 잰다** (2026-09-25). 경계 모서리·부피 분석은 카탈로그와 게이트(solid-check·
   * catalog-check)만 읽는데, 게임·에디터가 조각 이천 개를 지을 때마다 돌아 로딩을 몇 초씩 먹었다.
   * `stats` 를 처음 읽는 순간 한 번 계산한다. 개발 서버에서는 아래 경고를 위해 바로 잰다 */
  const computeStats = (): ModelStats => {
    const stats = analyze(slots, writer.parts, writer.openParts, writer.flippedParts, writer.boxes);

    /* **크기는 movable 부품까지 합쳐 잰다** (2026-09-16). analyze 는 정적 slots 만
     * 보기 때문에, 통째가 여닫이인 조각(소반·방석·액자)은 0×0×0, 다리가 여닫이인
     * 교자상은 상판 높이만으로 재어졌다 — 치수 검사의 거짓 음성. 화면에 그려지는
     * 것 기준으로 맞춘다. */
    if (movables.size) {
      const allBox = new THREE.Box3();
      for (const m of drawn) {
        m.geometry.computeBoundingBox();
        if (m.geometry.boundingBox) allBox.union(m.geometry.boundingBox);
      }
      for (const [, mv] of movables) {
        for (const [, geo] of mv) {
          geo.computeBoundingBox();
          if (geo.boundingBox) allBox.union(geo.boundingBox);
        }
      }
      const ms = new THREE.Vector3();
      allBox.getSize(ms);
      stats.size = [+ms.x.toFixed(3), +ms.y.toFixed(3), +ms.z.toFixed(3)];
    }

    // **양면(DoubleSide) 재질로 그리는 슬롯은 투과 검사에서 뺀다.**
    //
    // 열린 몸체와 뒤집힌 면이 화면에서 문제가 되는 이유는 뒷면 컬링 때문에 앞면이 사라져
    // 속이 보인다는 것이다. 그런데 양면 재질은 뒷면도 그리므로 그 증상이 애초에 생기지
    // 않는다 — 종이·가죽막·한지·그림면·유리가 전부 그렇고, 그것들은 두께 없이 그리는 게
    // 정상이다.
    //
    // 이 필터가 없으면 족자·병풍·무고·장구·등롱·부적판이 전부 위반으로 잡힌다(실제로
    // 그랬다). 그러면 게이트가 항상 실패하고, 항상 실패하는 게이트는 아무도 안 본다.
    // 슬롯마다 thinSurface() 를 붙이는 것보다 여기서 한 번 거르는 것이 정확하다 —
    // 판단 근거가 「재질이 양면인가」 하나뿐이기 때문이다.
    const twoSided = (slot: Slot) => {
      const m = mats.get(slot);
      return !!m && 'side' in m && (m as THREE.Material).side === THREE.DoubleSide;
    };
    stats.openParts = stats.openParts.filter((p) => !twoSided(p.slot));
    stats.flippedParts = stats.flippedParts.filter((p) => !twoSided(p.slot));
    return stats;
  };
  let statsMemo: ModelStats | undefined;

  // 개발 중에 바로 알아야 한다 — 카탈로그를 열어야 보이는 진단은 놓친다.
  // 게이트는 tools/solid-check.mjs 가 CI 로 막고, 이건 즉시 알림이다.
  const stats = import.meta.env?.DEV ? (statsMemo = computeStats()) : undefined;
  if (stats && (stats.openParts.length || stats.flippedParts.length)) {
    console.error(
      `[투과] ${piece.id}: 닫히지 않은 몸체 ${stats.openParts.length}개, 뒤집힌 면 ${stats.flippedParts.length}개`,
      { open: stats.openParts, flipped: stats.flippedParts },
    );
  }

  if (!piece.glb) fit.setFromObject(group);
  return { group, get stats() { return (statsMemo ??= computeStats()); }, fit };
}
